import "./searchbox.css";
import React, { forwardRef, useImperativeHandle } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTimes, faHistory } from "@fortawesome/free-solid-svg-icons";
import useSearchLogic   from "./searchLogic";
import { SEARCH_OPTIONS, Content } from "../../data/registry";

export interface SearchBoxHandle {
  search: () => void;
  feelingLucky: () => void;
  getValue: () => string;
}

const SearchBox = forwardRef<SearchBoxHandle>(function SearchBox(_props, ref) {
  const iconStyle: React.CSSProperties = {
    verticalAlign: "middle",
    marginRight:   10,
    fontSize:      13,
    color:        "#aaa",
  };

  const removeBtnStyle: React.CSSProperties = {
    fontSize:      13,
    color:        "#555",
    border:        0,
    outline:      "none",
    background:  "transparent",
    padding:     "0 0 0 10px",
    cursor:     "pointer",
    whiteSpace: "nowrap",
    flexShrink:  0,
  };

  const {
    value, setValue,
    show, hide,
    open, activeIndex,
    clear, onKeyDown, search,
  } = useSearchLogic({ options: SEARCH_OPTIONS });

  /* "I'm Feeling Lucky" keeps its custom logic */
  const feelingLucky = () => {
    const query = value.trim();

    if (!query) {
      const rand = SEARCH_OPTIONS[Math.floor(Math.random() * SEARCH_OPTIONS.length)].value;
      window.location.href = `/${rand}`;
      return;
    }

    const hit = Content.find(c => c.category.toLowerCase() === query.toLowerCase());
    if (hit) {
      window.location.href = hit.link;
    } else {
      search();
    }
  };

  /* expose methods so parent (Home) can call search/feelingLucky */
  useImperativeHandle(ref, () => ({
    search,
    feelingLucky,
    getValue: () => value,
  }));

  const [hiddenOptions, setHiddenOptions] = React.useState<Set<string>>(new Set());

  const removeOption = (name: string) => {
    setHiddenOptions((prev) => new Set(prev).add(name));
  };

  return (
    <div className="search-box">
      <div className="search-cont">
        <FontAwesomeIcon className="fa fa-search" icon={faSearch} />

        <div className={`search${open ? " search-open" : ""}`}>
          <div className="search-value">
            <input
              placeholder=" "
              autoComplete="off"
              className="search-input"
              role="combobox"
              aria-expanded={open}
              aria-label="Search"
              aria-autocomplete="list"
              aria-controls="search-listbox"
              aria-activedescendant={activeIndex >= 0 ? `search-option-${activeIndex}` : undefined}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onFocus={show}
              onBlur={hide}
              onKeyDown={onKeyDown}
            />
          </div>

          <div
            className="search-select"
            style={{ display: open ? "block" : "none" }}
          >
            <div className="search-options" id="search-listbox" role="listbox">
              {SEARCH_OPTIONS.map((o, i) =>
                hiddenOptions.has(o.name) ? null : (
                  <div
                    key={o.name}
                    id={`search-option-${i}`}
                    role="option"
                    aria-selected={i === activeIndex}
                    className={`search-option${i === activeIndex ? " search-option-active" : ""}`}
                  >
                    <span>
                      <FontAwesomeIcon className="fas" icon={faHistory} style={iconStyle} />
                      <a href={`/${o.value}`}>
                        {o.name}
                      </a>
                      <span>
                        <button
                          className="remove-btn"
                          style={removeBtnStyle}
                          onClick={() => removeOption(o.name)}
                        >
                          Remove
                        </button>
                      </span>
                    </span>
                  </div>
                )
              )}
            </div>

            <div className="search-btns" style={{ paddingTop: 20, paddingBottom: 30 }}>
              <input
                className="search-btn sw"
                type="button"
                value="Search Website"
                onClick={search}
              />
              <input
                className="search-btn ifl"
                type="button"
                value="I'm Feeling Lucky"
                onClick={feelingLucky}
              />
            </div>
          </div>
        </div>

        <FontAwesomeIcon
          className="fa fa-times clear-icon"
          icon={faTimes}
          title="Clear"
          onClick={clear}
        />
      </div>
    </div>
  );
});

export default SearchBox;
